import type { GridMap } from "./GridMap";
import type { MissionKey, MissionLevelMetadata, ResurrectionSector } from "./missionTypes";

export const GRID_SIZE = 64;
export const CELL_SIZE = 2;

export type LevelMode = "procedural" | "mission";

export enum TileType {
  Wall = 0,
  Floor = 1,
  Toxic = 2,
  Door = 3,
  SecretDoor = 4,
  Obstacle = 5,
}

export type Point = Readonly<{ x: number; y: number }>;

export type RoomKind =
  "start" | "encounter" | "hub" | "supply" | "secret" | "tech" | "toxic" | "dark" | "finale";

export interface Room {
  readonly shape?: "rectangle" | "octagon" | "cross" | "pillared" | "cave";
  readonly id: RoomKind;
  readonly name: string;
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
  readonly center: Point;
  readonly required: boolean;
}

export type MissionEdgeKind = "main" | "branch" | "secret" | "loop";

export interface MissionNode {
  readonly id: RoomKind;
  readonly name: string;
  readonly required: boolean;
}

export interface MissionEdge {
  readonly from: RoomKind;
  readonly to: RoomKind;
  readonly kind: MissionEdgeKind;
}

export interface MissionGraphData {
  readonly nodes: readonly MissionNode[];
  readonly edges: readonly MissionEdge[];
  readonly mainPath: readonly RoomKind[];
}

export interface DoorSpawn {
  readonly id: string;
  readonly cell: Point;
  readonly cells: readonly Point[];
  readonly position: Point;
  readonly orientation: "x" | "y";
  readonly secret: boolean;
  readonly autoClose: boolean;
  readonly requiredKey?: MissionKey;
}

export type EnemyKind =
  | "rustSentinel"
  | "sporeCrawler"
  | "guard"
  | "stalker"
  | "boneHound"
  | "choirAcolyte"
  | "forgeWarden"
  | "abyssTyrant";

export interface EnemySpawn {
  readonly id: string;
  readonly kind: EnemyKind;
  readonly cell: Point;
  readonly roomId: string;
  readonly resurrectionSector?: ResurrectionSector;
  readonly resurrectable?: boolean;
}

export type PickupKind = "health" | "armor" | "bullets" | "shells" | "shotgun" | "rifle" | "rifleAmmo";

export interface PropSpawn {
  readonly id: string;
  readonly kind: "crate" | "pillar";
  readonly cell: Point;
}

export interface PickupSpawn {
  readonly id: string;
  readonly kind: PickupKind;
  readonly cell: Point;
  readonly roomId: string;
}

export interface LevelData {
  readonly props?: readonly PropSpawn[];
  readonly mode: LevelMode;
  readonly seed: string;
  readonly generationSeed: string;
  readonly attempt: number;
  readonly grid: GridMap;
  readonly graph: MissionGraphData;
  readonly rooms: readonly Room[];
  readonly corridors: readonly (readonly Point[])[];
  readonly doors: readonly DoorSpawn[];
  readonly enemies: readonly EnemySpawn[];
  readonly pickups: readonly PickupSpawn[];
  readonly playerStart: Point;
  readonly exit: Point;
  readonly secretRoomId: RoomKind;
  readonly mission?: MissionLevelMetadata;
}

export interface ValidationResult {
  readonly valid: boolean;
  readonly errors: readonly string[];
}

export function pointKey(point: Point): string {
  return `${point.x},${point.y}`;
}

export function manhattanDistance(first: Point, second: Point): number {
  return Math.abs(first.x - second.x) + Math.abs(first.y - second.y);
}
